import type { ReactNode } from 'react';
import dayjs from 'dayjs';
import type { OptionBuyingStrategy, TradingStrategyForPortfolio } from '@einhaus/project314-types';
import Button from '@/components/UI/Button';
import Callout from '@/components/UI/Callout';
import StatusBadge from '@/components/UI/StatusBadge';
import InactiveStrategyCallout from './InactiveStrategyCallout';
import type { EffectivePositionSizing, StrategySubscriptionReadiness } from './strategySubscription';

interface StrategySubscriptionDetailsProps {
    strategy: TradingStrategyForPortfolio;
    readiness: StrategySubscriptionReadiness;
    sizing: EffectivePositionSizing;
    openPositionCount: number;
    tradingProfileName: string | null;
    optionBuyingStrategiesById: ReadonlyMap<number, OptionBuyingStrategy>;
    onConfigure: () => void;
}

const DetailSection = ({ title, children }: { title: string; children: ReactNode }) => (
    <section className="space-y-1.5">
        <h6 className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{title}</h6>
        {children}
    </section>
);

const DetailValue = ({ children }: { children: ReactNode }) => <p className="text-sm text-zinc-200">{children}</p>;

const formatTimestamp = (timestamp: number): string => dayjs.unix(timestamp).format('MMM D, YYYY h:mm A');

/**
 * Everything the subscription row deliberately keeps out of its cells: the full sizing policy, the
 * exact live-entry selections, and the provenance of the deployment. Rendered inside the expanded
 * table row and inside each mobile card.
 */
const StrategySubscriptionDetails = ({
    strategy,
    readiness,
    sizing,
    openPositionCount,
    tradingProfileName,
    optionBuyingStrategiesById,
    onConfigure
}: StrategySubscriptionDetailsProps) => {
    const universeSnapshot = strategy.entityUniverseSnapshot;

    return (
        <div className="space-y-4 p-4">
            {!readiness.isStrategyGloballyActive && (
                <InactiveStrategyCallout strategyId={strategy.id}>
                    {strategy.isActive
                        ? 'It is switched on for this portfolio, but it will not open positions until the strategy is activated globally.'
                        : 'It cannot be switched on for this portfolio until the strategy is activated globally.'}
                </InactiveStrategyCallout>
            )}

            {readiness.setupReasons.length > 0 && (
                <Callout
                    title="This subscription needs setup before it can take live entries"
                    variant={strategy.isActive && readiness.isStrategyGloballyActive ? 'error' : 'warning'}
                >
                    <ul className="ml-4 list-disc space-y-0.5">
                        {readiness.setupReasons.map((reason) => (
                            <li key={reason}>{reason}</li>
                        ))}
                    </ul>
                    <Button className="mt-2" size="sm" variant="secondary" onClick={onConfigure}>
                        Configure deployment
                    </Button>
                </Callout>
            )}

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <DetailSection title="Position sizing">
                    <div className="flex items-center gap-2">
                        <StatusBadge status={sizing.isCustom ? 'Custom' : 'Portfolio'} tone={sizing.isCustom ? 'accent' : 'neutral'} />
                        <span className="text-xs text-zinc-500">
                            {sizing.isCustom ? 'Overrides the portfolio defaults' : 'Inherited from the portfolio'}
                        </span>
                    </div>
                    {sizing.isMissing ? (
                        <DetailValue>
                            <span className="text-yellow-400">No sizing rule applies to this strategy.</span>
                        </DetailValue>
                    ) : (
                        <ul className="space-y-0.5">
                            {sizing.ruleDescriptions.map((description) => (
                                <li className="text-sm text-zinc-200" key={description}>
                                    {description}
                                </li>
                            ))}
                        </ul>
                    )}
                </DetailSection>

                <DetailSection title="Live entry selections">
                    <DetailValue>Trading profile: {tradingProfileName ?? 'Not linked'}</DetailValue>
                    {readiness.isOptionsStrategy && (
                        <DetailValue>
                            Option buying strategies:{' '}
                            {strategy.optionBuyingStrategyIds?.length
                                ? strategy.optionBuyingStrategyIds
                                      .map((id) => optionBuyingStrategiesById.get(id)?.name ?? `OBS #${id}`)
                                      .join(', ')
                                : 'None selected'}
                        </DetailValue>
                    )}
                    <DetailValue>
                        Exit candidates:{' '}
                        {strategy.exitCandidateIds?.length ? strategy.exitCandidateIds.map((id) => `#${id}`).join(', ') : 'None selected'}
                    </DetailValue>
                </DetailSection>

                <DetailSection title="Deployment record">
                    <DetailValue>Revision {strategy.selectionRevision ?? 0}</DetailValue>
                    <DetailValue>
                        {strategy.selectionUpdatedAt
                            ? `Updated ${formatTimestamp(strategy.selectionUpdatedAt)}`
                            : 'Never updated since subscribing'}
                    </DetailValue>
                    <DetailValue>
                        {openPositionCount > 0
                            ? `${openPositionCount.toLocaleString()} open position${openPositionCount === 1 ? '' : 's'}`
                            : 'No open positions'}
                    </DetailValue>
                </DetailSection>

                {universeSnapshot && (
                    <DetailSection title="Entity universe">
                        <DetailValue>
                            {universeSnapshot.source === 'entityList'
                                ? `Entity list${universeSnapshot.sourceEntityListId ? ` #${universeSnapshot.sourceEntityListId}` : ''}`
                                : 'Broad monitored entities'}
                        </DetailValue>
                        <DetailValue>
                            {universeSnapshot.entities.length.toLocaleString()} entities frozen{' '}
                            {formatTimestamp(universeSnapshot.capturedAt)}
                        </DetailValue>
                    </DetailSection>
                )}
            </div>
        </div>
    );
};

export default StrategySubscriptionDetails;
